import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { cores } from '../../theme';
import {
    Typography, Container, Box, Button, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Alert, Chip, IconButton, Avatar, Fade, useTheme
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    ArrowBack, Add as AddIcon, Edit as EditIcon,
    Person as PersonIcon
} from '@mui/icons-material';

const ProfessoresGerenciamentoPagina = () => {
    const [professores, setProfessores] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const navegar = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        const buscarProfessores = async () => {
            try {
                const resposta = await api.get('/api/usuarios?cargo=PROFESSOR');
                setProfessores(resposta.data);
            } catch (err) {
                setErro('Falha ao carregar a lista de professores.');
                console.error(err);
            } finally {
                setCarregando(false);
            }
        };
        buscarProfessores();
    }, []);

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Gerenciamento de Professores
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                Visualize, edite e crie novos professores
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                    }}>
                        <Box>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    color: cores.primaria.principal,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                }}
                            >
                                Lista de Professores
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {professores.length} {professores.length === 1 ? 'professor encontrado' : 'professores encontrados'}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            component={RouterLink}
                            to="/professores/criar"
                            startIcon={<AddIcon />}
                            sx={{
                                bgcolor: cores.primaria.principal,
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    bgcolor: cores.primaria.escuro,
                                }
                            }}
                        >
                            Cadastrar Professor
                        </Button>
                    </Box>

                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Fade in={true} timeout={500}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                overflow: 'hidden',
                                border: `1px solid ${cores.cinza.claro}`,
                            }}
                        >
                            <Table>
                                <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Nome Completo</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Admin?</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: cores.primaria.principal }}>Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {professores.map((prof, index) => (
                                        <Fade in={true} timeout={300 + index * 100} key={prof.id}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: '#f8fafc',
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Avatar
                                                            sx={{
                                                                mr: 2,
                                                                bgcolor: 'transparent',
                                                                color: cores.primaria.principal,
                                                                border: `2px solid ${cores.cinza.claro}`,
                                                                width: 36,
                                                                height: 36
                                                            }}
                                                        >
                                                            <PersonIcon />
                                                        </Avatar>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '1rem'
                                                            }}
                                                        >
                                                            {prof.nome_completo}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            color: cores.cinza.escuro,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {prof.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={prof.status}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 600,
                                                            bgcolor: prof.status === 'ATIVO' ? '#dcfce7' : '#fef9c3',
                                                            color: prof.status === 'ATIVO' ? '#166534' : '#854d0e',
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={prof.is_admin ? 'Sim' : 'Não'}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 600,
                                                            bgcolor: prof.is_admin ? `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)` : '#f1f5f9',
                                                            color: prof.is_admin ? cores.primaria.principal : cores.cinza.medio,
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        component={RouterLink}
                                                        to={`/professores/editar/${prof.id}`}
                                                        startIcon={<EditIcon />}
                                                        sx={{
                                                            borderRadius: 2,
                                                            borderColor: cores.cinza.medio,
                                                            color: cores.primaria.principal,
                                                            '&:hover': {
                                                                borderColor: cores.primaria.principal,
                                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.04)`
                                                            }
                                                        }}
                                                    >
                                                        Editar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        </Fade>
                                    ))}
                                    {professores.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" sx={{ color: cores.cinza.medio }}>
                                                    Nenhum professor encontrado.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Fade>
                </Paper>
            </Fade>
        </Container>
    );
};

export default ProfessoresGerenciamentoPagina;